import { useCallback, useEffect, useMemo, useState } from "react";
import type { CatalogIndex, CatalogItemView } from "../../data/local/catalog";
import { loadCatalogIndex, hydrateCatalog } from "../../data/local/catalog";
import { DiscountDialog } from "./DiscountDialog";
import { StatutoryDiscount } from "./StatutoryDiscount";
import { computeStatutoryDiscount } from "../../domain/pricing/statutory";
import { sum } from "../../domain/money/money";
import type { DiscountInput } from "../../domain/journal/finalize";
import { uuidv7 } from "../../platform/id";
import type { LocalModifier, LocalVariant } from "../../data/local/db";
import { getMeta, setMeta, META_KEYS } from "../../data/local/db";
import type { TaxConfig } from "../../domain/pricing/price-cart";
import type { CurrencyFormat } from "../../domain/money/format";
import type { TenderEntry } from "../../domain/journal/tender";
import { commitSale, loadSaleForReceipt } from "../../data/local/finalize-sale";
import {
  composeReceipt,
  type ReceiptDocument,
  type ReceiptBusiness,
} from "../../platform/receipt/compose";
import { ScreenReceipt } from "../../platform/receipt/ScreenReceipt";
import { useCart } from "./useCart";
import { MenuGrid } from "./MenuGrid";
import { ItemSheet } from "./ItemSheet";
import { Cart } from "./Cart";
import { CartBar } from "./CartBar";
import { useIsPhone } from "../useMediaQuery";
import { OrderType } from "./OrderType";
import { TenderSheet } from "./TenderSheet";
import { Slot } from "../slots/Slot";

/**
 * The till.
 *
 * Composition only — the money lives in the domain modules and the persistence
 * lives in the data layer. This file decides what is on screen.
 */

export interface TillConfig {
  tax: TaxConfig;
  /** Basis points, e.g. 2000 = 20% (RA 9994 / RA 10754). */
  statutoryDiscountBp: number;
  currency: CurrencyFormat;
  orderTypes: string[];
  orderTypeLabels: Record<string, string>;
  tenderTypes: string[];
  tenderLabels: Record<string, string>;
  /** Reuses the receipt's shape rather than redeclaring it — one definition. */
  business: ReceiptBusiness;
  complianceNotice: string;
  timezone: string;
  activeModules: string[];
}

interface TillProps {
  config: TillConfig;
  staffId: string;
  staffName: string;
  terminalId: string;
  shiftId: string;
  series: string;
  /**
   * Ask a manager to approve something, resolving to their id or null.
   *
   * Supplied by the shell so ONE approval dialog serves discounts, voids,
   * refunds and cash variances — four features that must not drift apart in
   * how they record who allowed what.
   */
  onRequestManager(action: string): Promise<string | null>;
  /**
   * Bumped whenever the menu is edited, so the grid reloads.
   *
   * The catalog index is built once on mount and held in memory, because
   * Principle V forbids I/O between a tap and the cart. That makes an edit made
   * elsewhere in the shell invisible here until a remount — so the shell says
   * when something changed rather than this component polling for it.
   */
  catalogVersion?: number;
}

type Overlay =
  | { kind: "none" }
  | { kind: "item"; view: CatalogItemView }
  | { kind: "tender" }
  | { kind: "discount" }
  | { kind: "statutory" }
  | { kind: "receipt"; document: ReceiptDocument };

export function Till({
  config,
  staffId,
  staffName,
  terminalId,
  shiftId,
  series,
  onRequestManager,
  catalogVersion = 0,
}: TillProps) {
  const [index, setIndex] = useState<CatalogIndex | null>(null);
  const [overlay, setOverlay] = useState<Overlay>({ kind: "none" });
  const [orderType, setOrderType] = useState(config.orderTypes[0] ?? "takeout");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cartOpen, setCartOpen] = useState(false);
  const isPhone = useIsPhone();

  // Scoped to the shift so an order left over from a previous, closed shift
  // cannot reappear over today's opening float (FR-044).
  const cart = useCart(config.tax, config.statutoryDiscountBp, shiftId);

  // Load from the local replica first so the till renders with no network. Only
  // then try to refresh — and a failure there is not an error, it is Tuesday.
  useEffect(() => {
    let cancelled = false;

    void (async () => {
      const local = await loadCatalogIndex();
      if (!cancelled) setIndex(local);

      try {
        const response = await fetch("/api/catalog");
        if (!response.ok) return;
        await hydrateCatalog(await response.json());
        const refreshed = await loadCatalogIndex();
        if (!cancelled) setIndex(refreshed);
      } catch {
        // Offline. The local replica is authoritative for trading.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [catalogVersion]);

  const addConfigured = useCallback(
    (view: CatalogItemView, variant: LocalVariant, modifiers: LocalModifier[]) => {
      cart.addItem(view, variant, modifiers);
      setOverlay({ kind: "none" });
    },
    [cart],
  );

  /**
   * The discount as records to store beside the sale.
   *
   * One row PER LINE, matching how the discount was priced. A single sale-level
   * row would say the reduction happened somewhere other than where the
   * arithmetic put it, and a refund reading `lineTotal` would then disagree
   * with the discount record about what the customer was charged.
   */
  const discountRecords = useCallback((): DiscountInput[] => {
    // A module's grant is its own row on its line, naming the module, beside
    // whichever core discount is also in force. The amounts are the same split
    // the cart priced with (line-grant.ts), so the rows always add up to what
    // the lines say was taken off.
    const grantRows: DiscountInput[] = cart.discountParts
      .filter((part) => part.grant > 0 && cart.grants[part.lineId])
      .map((part) => ({
        id: uuidv7(),
        saleLineId: part.lineId,
        type: "promotional" as const,
        basis: cart.grants[part.lineId]!.label,
        rateBp: 0,
        amount: part.grant,
        coveredCustomers: null,
        totalCustomers: null,
        cardholderName: null,
        cardholderId: null,
        authorizedBy: null,
        grantedByModule: cart.grants[part.lineId]!.moduleId,
      }));

    const promo = cart.promo;
    if (promo) {
      return [
        ...grantRows,
        ...cart.discountParts
          .filter((part) => part.promo > 0)
          .map((part) => ({
            id: uuidv7(),
            saleLineId: part.lineId,
            type: "promotional" as const,
            basis: null,
            rateBp: promo.rateBp,
            amount: part.promo,
            coveredCustomers: null,
            totalCustomers: null,
            cardholderName: null,
            cardholderId: null,
            authorizedBy: promo.authorizedBy,
          })),
      ];
    }

    const statutory = cart.statutory;
    const statutoryResult = cart.statutoryResult;
    if (statutory && statutoryResult) {
      // Sale-level, not per-line (saleLineId: null) — the discount applies
      // to a covered SHARE of the eligible amount across the whole order,
      // not to any one line, and finalize.ts's statutory branch re-derives
      // the full computation from coveredCustomers/totalCustomers/rateBp
      // rather than trusting this amount directly (Principle VII).
      return [
        ...grantRows,
        {
          id: uuidv7(),
          saleLineId: null,
          type: statutory.discountType,
          basis: statutory.discountType === "senior_citizen" ? "RA 9994" : "RA 10754",
          rateBp: config.statutoryDiscountBp,
          amount: statutoryResult.discountAmount,
          coveredCustomers: statutory.coveredCustomers,
          totalCustomers: statutory.totalCustomers,
          cardholderName: statutory.cardholderName,
          cardholderId: statutory.cardholderId,
          authorizedBy: statutory.authorizedBy,
        },
      ];
    }

    return grantRows;
  }, [cart.promo, cart.discountParts, cart.grants, cart.statutory, cart.statutoryResult, config.statutoryDiscountBp]);

  /** A discount is only real once a manager has put their name to it. */
  const applyDiscount = useCallback(
    async (rateBp: number) => {
      // Close this dialog FIRST. The approval prompt belongs to the shell and
      // renders beneath the till's own overlay, so leaving this one mounted
      // puts a disabled Apply button on top of the manager's PIN pad.
      setOverlay({ kind: "none" });

      const managerId = await onRequestManager(`Take ${rateBp / 100}% off this order`);
      if (!managerId) return;

      cart.applyPromo(rateBp, managerId);
    },
    [cart, onRequestManager],
  );

  /** Mirrors applyDiscount above — approval first, then the discount is real. */
  const applyStatutory = useCallback(
    async (input: Omit<import("./useCart").StatutoryDiscount, "authorizedBy">) => {
      setOverlay({ kind: "none" });

      const label = input.discountType === "senior_citizen" ? "Senior Citizen" : "PWD";
      const managerId = await onRequestManager(`Apply a ${label} discount for ${input.cardholderName}`);
      if (!managerId) return;

      cart.applyStatutory({ ...input, authorizedBy: managerId });
    },
    [cart, onRequestManager],
  );

  /** Live preview while the statutory dialog's fields are still being edited. */
  const previewStatutory = useCallback(
    (input: { discountType: "senior_citizen" | "pwd"; coveredCustomers: number; totalCustomers: number }) => {
      const eligibleAmount = sum(
        cart.priced.lines.filter((l) => l.statutoryEligible).map((l) => l.lineTotal),
      );
      if (eligibleAmount <= 0) return null;
      try {
        return computeStatutoryDiscount({
          eligibleAmount,
          coveredCustomers: input.coveredCustomers,
          totalCustomers: input.totalCustomers,
          tax: config.tax,
          statutoryRateBp: config.statutoryDiscountBp,
        });
      } catch {
        return null;
      }
    },
    [cart.priced.lines, config.tax, config.statutoryDiscountBp],
  );

  const complete = useCallback(
    async (tenders: TenderEntry[]) => {
      setBusy(true);
      setError(null);
      try {
        const { sale } = await commitSale(
          {
            terminalId,
            shiftId,
            staffId,
            orderType,
            customerName: null,
            lines: cart.pricingInputs,
            tenders,
            discounts: discountRecords(),
            tax: config.tax,
            activeModules: config.activeModules,
            timezone: config.timezone,
          },
          series,
          cart.annotations,
        );

        const stored = await loadSaleForReceipt(sale.id);
        if (!stored) throw new Error("Sale committed but could not be read back");

        const document = composeReceipt({
          ...stored,
          cashierName: staffName,
          business: config.business,
          complianceNotice: config.complianceNotice,
          orderTypeLabels: config.orderTypeLabels,
        });

        cart.clear();
        setOverlay({ kind: "receipt", document });
      } catch (caught) {
        setError(caught instanceof Error ? caught.message : "Could not record the sale");
      } finally {
        setBusy(false);
      }
    },
    [cart, config, orderType, series, shiftId, staffId, staffName, terminalId],
  );

  const showTax = useMemo(() => config.tax.regime === "vat", [config.tax.regime]);

  if (!index) return <div className="till till--loading" />;

  return (
    <div className={`till${isPhone ? " till--phone" : ""}`}>
      <div className="till__main">
        <div className="till__toolbar">
          <OrderType
            types={config.orderTypes}
            labels={config.orderTypeLabels}
            value={orderType}
            onChange={setOrderType}
          />
        </div>

        <MenuGrid
          index={index}
          currency={config.currency}
          onAdd={(view) => cart.addItem(view)}
          onConfigure={(view) => setOverlay({ kind: "item", view })}
        />
      </div>

      {isPhone ? (
        <CartBar
          cart={cart}
          currency={config.currency}
          showTax={showTax}
          expanded={cartOpen}
          onToggle={() => setCartOpen((open) => !open)}
          onTender={() => {
            setCartOpen(false);
            setOverlay({ kind: "tender" });
          }}
          onDiscount={() => {
            setCartOpen(false);
            setOverlay({ kind: "discount" });
          }}
        />
      ) : (
        <Cart
          cart={cart}
          currency={config.currency}
          showTax={showTax}
          onTender={() => setOverlay({ kind: "tender" })}
          onDiscount={() => setOverlay({ kind: "discount" })}
        />
      )}

      {error && (
        <p className="till__error" role="alert">
          {error}
        </p>
      )}

      {overlay.kind !== "none" && (
        <div className="till__overlay">
          {overlay.kind === "item" && (
            <ItemSheet
              view={overlay.view}
              currency={config.currency}
              onAdd={addConfigured}
              onCancel={() => setOverlay({ kind: "none" })}
            />
          )}

          {overlay.kind === "discount" && (
            <DiscountDialog
              lines={cart.priced.lines.map((line) => ({
                lineId: line.lineId,
                lineSubtotal: line.lineSubtotal,
              }))}
              currency={config.currency}
              currentRateBp={cart.promo?.rateBp ?? null}
              onApply={(rateBp) => void applyDiscount(rateBp)}
              onRemove={() => {
                cart.removePromo();
                setOverlay({ kind: "none" });
              }}
              onCancel={() => setOverlay({ kind: "none" })}
              onSwitchToStatutory={() => setOverlay({ kind: "statutory" })}
            />
          )}

          {overlay.kind === "statutory" && (
            <StatutoryDiscount
              currency={config.currency}
              eligibleAmount={sum(
                cart.priced.lines.filter((l) => l.statutoryEligible).map((l) => l.lineTotal),
              )}
              preview={previewStatutory}
              currentDiscount={cart.statutory}
              onApply={(input) => void applyStatutory(input)}
              onRemove={() => {
                cart.removeStatutory();
                setOverlay({ kind: "none" });
              }}
              onCancel={() => setOverlay({ kind: "none" })}
            />
          )}

          {overlay.kind === "tender" && (
            <TenderSheet
              due={cart.priced.total}
              currency={config.currency}
              tenderTypes={config.tenderTypes}
              tenderLabels={config.tenderLabels}
              busy={busy}
              onComplete={complete}
              onCancel={() => setOverlay({ kind: "none" })}
            />
          )}

          {overlay.kind === "receipt" && (
            <div className="till__receipt">
              <ScreenReceipt document={overlay.document} currency={config.currency} />
              <Slot id="till.postSale" saleId={overlay.document.documentNumber} />
              <div className="till__receipt-actions">
                <button type="button" onClick={() => window.print()}>
                  Print
                </button>
                <button
                  type="button"
                  className="is-primary"
                  onClick={() => setOverlay({ kind: "none" })}
                >
                  Next customer
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** Remembers the last order type across reloads. */
export async function rememberOrderType(value: string): Promise<void> {
  await setMeta("till.orderType", value);
}

export async function recallOrderType(fallback: string): Promise<string> {
  return (await getMeta<string>("till.orderType")) ?? fallback;
}

export { META_KEYS };
export default Till;
